import React, { ReactNode, createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { SharedValue, useSharedValue } from 'react-native-reanimated';

import { Product } from '@/hooks/use-jurisdiction';

export type Position = { x: number; width: number };

type PositionsMap = Map<Product, Position>;

interface PositionsContextValue {
    setPosition: (product: Product, pos: Position) => void;
    getPosition: (product: Product) => Position | undefined;
    // Shared scroll offset used to synchronize scroll positions between the normal
    // ProductSwitcherChips component and the ProductSwitcherChipsTransition component.
    // This ensures both components maintain the same scroll state during transitions.
    scrollViewOffset: SharedValue<number>;
}

const ProductSwitcherChipsPositionsContext = createContext<PositionsContextValue | null>(null);
/**
 * Provider used to manage the layout for the product switcher chips.
 * It stores the positions of each product chip in a map, allowing to set and get positions
 * for each product.
 *
 * This is used to animate the transition animation of the chips when switching products.
 */
export const ProductSwitcherChipsPositionsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const positionsRef = useRef<PositionsMap>(new Map());
    const scrollViewOffset = useSharedValue(0);

    const setPosition = useCallback((product: Product, pos: Position) => {
        positionsRef.current.set(product, pos);
    }, []);

    const getPosition = useCallback((product: Product) => positionsRef.current.get(product), []);

    const value = useMemo(
        () => ({
            setPosition,
            getPosition,
            scrollViewOffset,
        }),
        [setPosition, getPosition, scrollViewOffset]
    );

    return (
        <ProductSwitcherChipsPositionsContext.Provider value={value}>
            {children}
        </ProductSwitcherChipsPositionsContext.Provider>
    );
};

export const useProductSwitcherChipsPositions = () => {
    const ctx = useContext(ProductSwitcherChipsPositionsContext);
    if (!ctx) {
        throw new Error('useProductSwitcherChipsPositions must be used inside ProductSwitcherChipsPositionsProvider');
    }
    return ctx;
};
